import React, { useState, useEffect, useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import { useParams, useHistory } from 'react-router-dom'
import axios from 'axios'
import ProposalHeader from './ProposalHeader'
import ProposalPreview from './ProposalPreview'
import AccordionEditor from './AccordionEditor'
import useProposal from './hooks/useProposal'
import useTheme from './hooks/useTheme'
import './styles/proposal.css'

const QuickMtcProposalBuilder = (props) => {
  const { id } = useParams()
  const history = useHistory()
  const url = props.url
  const [templates, setTemplates] = useState([])
  const [liveTheme, setLiveTheme] = useState(null)
  const themeSaveTimer = useRef(null)

  const {
    proposal,
    template,
    loading,
    saving,
    dirty,
    error,
    updateModuleOverride,
    updateModuleOverrides,
    toggleModule,
    reorderModules,
    updateCrmSnapshot,
    updateCrmSnapshots,
    updateJobsite,
    getModuleData,
    forceSave,
    variableDefinitions,
    resolvedVariables,
    updateVariable,
    resetVariables
  } = useProposal(id, url)

  // Initialize liveTheme from template
  useEffect(() => {
    if (template?.theme) {
      setLiveTheme(template.theme)
    }
  }, [template])

  // CSS variable injection — uses liveTheme so color picker changes are instant
  const themeObj = useTheme(liveTheme || template?.theme)

  useEffect(() => {
    if (!url) return
    axios.get(`${url}/api/mtc-proposals/templates`)
      .then(res => setTemplates(res.data || []))
      .catch(() => {})
  }, [url])

  // Save theme to the template (debounced)
  const handleThemeChange = useCallback((newTheme) => {
    setLiveTheme(newTheme)
    if (themeSaveTimer.current) clearTimeout(themeSaveTimer.current)
    const templateId = template?._id || proposal?.templateId?._id || proposal?.templateId
    if (!templateId || !url) return
    themeSaveTimer.current = setTimeout(() => {
      axios.put(`${url}/api/mtc-proposals/templates/${templateId}`, { theme: newTheme })
        .catch(err => console.error('Failed to save theme:', err))
    }, 800)
  }, [template, proposal, url])

  const handleTemplateChange = async (templateId) => {
    try {
      await axios.put(`${url}/api/mtc-proposals/${id}`, { templateId })
      window.location.reload()
    } catch (err) {
      console.error('Template change failed:', err)
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading proposal...</div>
      </div>
    )
  }

  if (error && !proposal) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 16 }}>
        <div>{error}</div>
        <button onClick={() => history.push('/mtc-proposals')}>Back to Proposals</button>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <ProposalHeader
        proposal={proposal}
        saving={saving}
        dirty={dirty}
        onSave={forceSave}
        onBack={() => history.push('/mtc-proposals')}
        url={url}
        templates={templates}
        onTemplateChange={handleTemplateChange}
      />
      <div className="proposal-builder">
        <div className="proposal-builder__preview">
          <ProposalPreview
            proposal={proposal}
            getModuleData={getModuleData}
            theme={themeObj}
            variables={resolvedVariables}
          />
        </div>
        <div className="proposal-builder__editor">
          <AccordionEditor
            proposal={proposal}
            getModuleData={getModuleData}
            updateModuleOverride={updateModuleOverride}
            updateModuleOverrides={updateModuleOverrides}
            toggleModule={toggleModule}
            reorderModules={reorderModules}
            updateCrmSnapshot={updateCrmSnapshot}
            updateCrmSnapshots={updateCrmSnapshots}
            theme={liveTheme || template?.theme}
            onThemeChange={handleThemeChange}
            variableDefinitions={variableDefinitions}
            resolvedVariables={resolvedVariables}
            onVariableChange={updateVariable}
            onResetVariables={resetVariables}
            proposalVariables={proposal?.variables || {}}
            url={url}
            updateJobsite={updateJobsite}
          />
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user
})

export default connect(mapStateToProps)(QuickMtcProposalBuilder)
